import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FiDollarSign, FiPlus, FiEdit2, FiTrash2, FiSearch, FiX, FiFilter,
  FiFileText, FiCalendar, FiCheck, FiEye, FiUpload, FiTruck, FiCreditCard
} from 'react-icons/fi';
import ItemSearchSelect from './common/ItemSearchSelect';

const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';

const StockImportsManagement = ({ onCreateNew }) => {
  const location = useLocation();
  const [imports, setImports] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  
  // View mode: 'all', 'imports', 'expenses'
  const [viewMode, setViewMode] = useState('all');
  
  // Expense modal
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    name: '',
    amount: '',
    expenseDate: new Date().toISOString().slice(0, 10),
    recipient: '',
    supplierId: '',
    expenseGroup: '',
    expenseGroupName: '',
    paymentMethod: 'cash',
    notes: ''
  });
  
  // Handle navigation state to open expense modal
  useEffect(() => {
    if (location.state?.openExpenseModal) {
      setShowExpenseModal(true);
      setViewMode('expenses');
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  const [expenseFile, setExpenseFile] = useState(null);
  const [expenseGroups, setExpenseGroups] = useState([]);
  const [showAddExpenseGroup, setShowAddExpenseGroup] = useState(false);
  const [newExpenseGroupName, setNewExpenseGroupName] = useState('');
  const [showAddExpenseSupplier, setShowAddExpenseSupplier] = useState(false);
  const [newExpenseSupplierName, setNewExpenseSupplierName] = useState('');
  
  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    supplier: '',
    startDate: '',
    endDate: '',
    expenseGroup: ''
  });
  const [suppliersList, setSuppliersList] = useState([]);
  const [salesAccounts, setSalesAccounts] = useState([]); // Danh sách tài khoản bán hàng
  
  // Statistics
  const [stats, setStats] = useState({
    filtered: { totalImports: 0, totalCost: 0, totalItems: 0 },
    today: { totalCost: 0, count: 0 },
    thisMonth: { totalCost: 0, count: 0 }
  });
  
  // Calculate expense stats from expenses array
  const expenseStats = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const thisMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
    
    let todayCost = 0, todayCount = 0;
    let monthCost = 0, monthCount = 0;
    let filteredCost = 0, filteredCount = 0;
    
    expenses.forEach(exp => {
      const expDate = exp.expenseDate?.slice(0, 10) || '';
      const expMonth = exp.expenseDate?.slice(0, 7) || '';
      const amount = exp.amount || 0;
      
      // Today
      if (expDate === today) {
        todayCost += amount;
        todayCount++;
      }
      
      // This month
      if (expMonth === thisMonth) {
        monthCost += amount;
        monthCount++;
      }
      
      // Filtered (already filtered by date from API)
      filteredCost += amount;
      filteredCount++;
    });
    
    return {
      today: { totalCost: todayCost, count: todayCount },
      thisMonth: { totalCost: monthCost, count: monthCount },
      filtered: { totalCost: filteredCost, count: filteredCount }
    };
  }, [expenses]);
  
  // Combined stats based on viewMode
  const displayStats = useMemo(() => {
    if (viewMode === 'imports') {
      // Only import stats
      return stats;
    } else if (viewMode === 'expenses') {
      // Only expense stats
      return {
        today: expenseStats.today,
        thisMonth: expenseStats.thisMonth,
        filtered: { 
          totalCost: expenseStats.filtered.totalCost, 
          totalImports: expenseStats.filtered.count,
          totalItems: 0 
        }
      };
    } else {
      // Combined stats (all)
      return {
        today: {
          totalCost: (stats.today?.totalCost || 0) + expenseStats.today.totalCost,
          count: (stats.today?.count || 0) + expenseStats.today.count
        },
        thisMonth: {
          totalCost: (stats.thisMonth?.totalCost || 0) + expenseStats.thisMonth.totalCost,
          count: (stats.thisMonth?.count || 0) + expenseStats.thisMonth.count
        },
        filtered: {
          totalCost: (stats.filtered?.totalCost || 0) + expenseStats.filtered.totalCost,
          totalImports: (stats.filtered?.totalImports || 0) + expenseStats.filtered.count,
          totalItems: stats.filtered?.totalItems || 0
        }
      };
    }
  }, [viewMode, stats, expenseStats]);
  
  // Selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedExpenseIds, setSelectedExpenseIds] = useState([]);
  
  // Modals
  const [viewingImport, setViewingImport] = useState(null);
  const [editingImport, setEditingImport] = useState(null);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // Mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [longPressTimer, setLongPressTimer] = useState(null);
  
  const listRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadSuppliers();
    loadExpenseGroups();
    loadSalesAccounts();
  }, []);

  const loadExpenses = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.supplier) params.append('supplierName', filters.supplier);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.expenseGroup) params.append('groupId', filters.expenseGroup);
      // Load all for combined view (limit=0 means no pagination)
      params.append('limit', '0');
      
      const response = await axios.get(`${API_URL}/expenses?${params.toString()}`);
      // Handle both old format (array) and new format (with data field)
      const expensesData = Array.isArray(response.data) ? response.data : (response.data.data || response.data || []);
      setExpenses(expensesData);
    } catch (error) {
      console.error('Error loading expenses:', error);
    }
  }, [filters.search, filters.supplier, filters.startDate, filters.endDate, filters.expenseGroup]);

  const loadStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.supplier) params.append('supplier', filters.supplier);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      
      const response = await axios.get(`${API_URL}/stock-imports/stats/summary?${params.toString()}`);
      setStats(response.data.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, [filters.search, filters.supplier, filters.startDate, filters.endDate]);

  const loadImports = useCallback(async (reset = false, cursorOverride = null) => {
    try {
      if (reset) {
        setLoading(true);
        setNextCursor(null);
      } else {
        setLoadingMore(true);
      }

      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.supplier) params.append('supplier', filters.supplier);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      const cursorToUse = cursorOverride !== null ? cursorOverride : (!reset ? nextCursor : null);
      if (cursorToUse) params.append('cursor', cursorToUse);
      params.append('limit', '20');

      const response = await axios.get(`${API_URL}/stock-imports?${params.toString()}`);
      const { data, hasMore: more, nextCursor: cursor } = response.data;

      if (reset) {
        setImports(data || []);
      } else {
        setImports(prev => [...prev, ...(data || [])]);
      }
      
      setHasMore(more);
      setNextCursor(cursor);
    } catch (error) {
      toast.error('Không thể tải danh sách nhập hàng');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filters.search, filters.supplier, filters.startDate, filters.endDate, nextCursor]);

  // Load imports and expenses when filters change
  useEffect(() => {
    loadImports(true);
    loadExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.supplier, filters.startDate, filters.endDate, filters.expenseGroup]);
  
  // Load stats separately when filters change
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const loadSuppliers = async () => {
    try {
      const response = await axios.get(`${API_URL}/suppliers`);
      const data = response.data.data || response.data || [];
      // Filter and ensure each supplier has _id (use name as fallback key for imports without _id)
      const suppliers = data.map(s => ({
        ...s,
        _id: s._id || s.name, // Use name as ID for suppliers from imports
      })).filter(s => s.name);
      setSuppliersList(suppliers);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const loadSalesAccounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/shop-settings`);
      if (response.data.success && response.data.data?.additionalAdmins) {
        // Chỉ lấy tài khoản bán hàng (role = 'sales')
        const accounts = response.data.data.additionalAdmins
          .filter(admin => admin.role === 'sales')
          .map(admin => ({
            _id: admin._id,
            username: admin.username,
            fullName: admin.fullName || '',
            role: admin.role,
            displayName: admin.fullName ? `${admin.fullName} - ${admin.username}` : admin.username
          }));
        setSalesAccounts(accounts);
      }
    } catch (error) {
      console.error('Error loading sales accounts:', error);
    }
  };

  const loadExpenseGroups = async () => {
    try {
      const response = await axios.get(`${API_URL}/expenses/groups`);
      setExpenseGroups(response.data || []);
    } catch (error) {
      console.error('Error loading expense groups:', error);
    }
  };

  // Infinite scroll observer
  const lastItemRef = useCallback(node => {
    if (loadingMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadImports(false);
      }
    });
    
    if (node) observerRef.current.observe(node);
  }, [loadingMore, hasMore, loadImports]);

  const handleDelete = async (id, reverseStock = false) => {
    try {
      await axios.delete(`${API_URL}/stock-imports/${id}?reverseStock=${reverseStock}`);
      setImports(prev => prev.filter(imp => imp._id !== id));
      setShowDeleteConfirm(null);
      loadExpenses(); // Reload expenses to remove linked shipping cost
      loadStats();
    } catch (error) {
      toast.error('Không thể xóa phiếu nhập');
    }
  };

  const handleBulkDelete = async (reverseStock = false) => {
    if (selectedIds.length === 0 && selectedExpenseIds.length === 0) return;
    
    try {
      // Delete imports
      if (selectedIds.length > 0) {
        await axios.post(`${API_URL}/stock-imports/bulk-delete`, {
          ids: selectedIds,
          reverseStock
        });
        setImports(prev => prev.filter(imp => !selectedIds.includes(imp._id)));
        setSelectedIds([]);
        loadExpenses(); // Reload expenses to remove linked shipping costs
      }
      
      // Delete expenses
      if (selectedExpenseIds.length > 0) {
        await Promise.all(selectedExpenseIds.map(id => 
          axios.delete(`${API_URL}/expenses/${id}`)
        ));
        setExpenses(prev => prev.filter(exp => !selectedExpenseIds.includes(exp._id)));
        setSelectedExpenseIds([]);
      }
      
      loadStats();
      toast.success('Đã xóa thành công');
    } catch (error) {
      toast.error('Không thể xóa các mục đã chọn');
    }
  };

  const handleUpdateImport = async (id, data) => {
    try {
      const formData = new FormData();
      if (data.importCode !== undefined) formData.append('importCode', data.importCode);
      if (data.supplier !== undefined) formData.append('supplier', data.supplier);
      if (data.notes !== undefined) formData.append('notes', data.notes);
      if (data.importDate) formData.append('importDate', data.importDate);
      if (data.paymentMethod) formData.append('paymentMethod', data.paymentMethod);
      if (data.shippingCost !== undefined) formData.append('shippingCost', data.shippingCost);
      if (data.removeInvoice) formData.append('removeInvoice', 'true');
      if (data.invoice) formData.append('invoice', data.invoice);
      if (data.items) formData.append('items', JSON.stringify(data.items));
      if (data.updateStock) formData.append('updateStock', 'true');

      const response = await axios.put(`${API_URL}/stock-imports/${id}`, formData);
      setImports(prev => prev.map(imp => 
        imp._id === id ? response.data.data : imp
      ));
      setEditingImport(null);
      loadStats(); // Reload stats after update
      loadExpenses(); // Reload expenses in case shipping cost was added/updated/removed
      toast.success('Đã cập nhật phiếu nhập');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể cập nhật phiếu nhập');
    }
  };

  // ==================== EXPENSE FUNCTIONS ====================
  
  const openExpenseModal = (expense = null) => {
    if (expense) {
      // Check if this is a shipping cost expense linked to an import
      if (expense.isShippingCost && expense.stockImportCode) {
        toast.info(`Chi phí vận chuyển này được liên kết với phiếu nhập ${expense.stockImportCode}. Để sửa, vui lòng sửa trong phiếu nhập.`);
        return;
      }
      setEditingExpense(expense);
      
      // Determine supplierId value
      let supplierIdValue = expense.supplierId?._id || expense.supplierId || '';
      
      // Check if recipient matches a staff member (when no supplierId)
      if (!supplierIdValue && expense.recipient) {
        const matchingStaff = salesAccounts.find(acc => 
          acc.displayName === expense.recipient || 
          acc.username === expense.recipient ||
          acc.fullName === expense.recipient
        );
        if (matchingStaff) {
          supplierIdValue = `staff:${matchingStaff.displayName}`;
        }
      }
      
      setExpenseForm({
        name: expense.name || '',
        amount: expense.amount?.toString() || '',
        expenseDate: expense.expenseDate ? new Date(expense.expenseDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
        recipient: expense.recipient || '',
        supplierId: supplierIdValue,
        expenseGroup: expense.expenseGroup?._id || expense.expenseGroup || '',
        expenseGroupName: expense.expenseGroupName || expense.expenseGroup?.name || '',
        paymentMethod: expense.paymentMethod || 'cash',
        notes: expense.notes || ''
      });
    } else {
      setEditingExpense(null);
      setExpenseForm({
        name: '',
        amount: '',
        expenseDate: new Date().toISOString().slice(0, 10),
        recipient: '',
        supplierId: '',
        expenseGroup: '',
        expenseGroupName: '',
        paymentMethod: 'cash',
        notes: ''
      });
    }
    setExpenseFile(null);
    setShowAddExpenseGroup(false);
    setShowAddExpenseSupplier(false);
    setShowExpenseModal(true);
  };

  const closeExpenseModal = () => {
    setShowExpenseModal(false);
    setEditingExpense(null);
    setExpenseFile(null);
  };

  const handleSaveExpense = async () => {
    if (!expenseForm.name.trim()) {
      toast.error('Vui lòng nhập tên khoản thanh toán');
      return;
    }
    if (!expenseForm.amount || parseFloat(expenseForm.amount) <= 0) {
      toast.error('Vui lòng nhập số tiền hợp lệ');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('name', expenseForm.name);
      formData.append('amount', expenseForm.amount);
      formData.append('expenseDate', expenseForm.expenseDate);
      formData.append('paymentMethod', expenseForm.paymentMethod);
      formData.append('notes', expenseForm.notes);
      
      if (showAddExpenseSupplier && newExpenseSupplierName.trim()) {
        formData.append('newSupplier', newExpenseSupplierName.trim());
      } else if (expenseForm.supplierId) {
        // Check if this is a staff member (prefixed with "staff:")
        if (expenseForm.supplierId.startsWith('staff:')) {
          const staffName = expenseForm.supplierId.replace('staff:', '');
          formData.append('recipient', staffName);
        } else {
          // Check if this is a real supplier (with MongoDB ObjectId) or from imports (name as ID)
          const selectedSupplier = suppliersList.find(s => s._id === expenseForm.supplierId);
          if (selectedSupplier) {
            if (selectedSupplier.isFromImport) {
              // Supplier from imports - save name as recipient
              formData.append('recipient', selectedSupplier.name);
            } else {
              // Real supplier - save ObjectId
              formData.append('supplierId', expenseForm.supplierId);
              formData.append('recipient', selectedSupplier.name);
            }
          }
        }
      }
      
      if (showAddExpenseGroup && newExpenseGroupName.trim()) {
        formData.append('newGroup', newExpenseGroupName.trim());
      } else if (expenseForm.expenseGroup) {
        formData.append('expenseGroup', expenseForm.expenseGroup);
        formData.append('expenseGroupName', expenseForm.expenseGroupName);
      }
      
      if (expenseFile) {
        formData.append('attachment', expenseFile);
      }

      if (editingExpense) {
        await axios.put(`${API_URL}/expenses/${editingExpense._id}`, formData);
        toast.success('Đã cập nhật khoản thanh toán');
      } else {
        await axios.post(`${API_URL}/expenses`, formData);
        toast.success('Đã thêm khoản thanh toán');
      }

      closeExpenseModal();
      loadExpenses();
      loadExpenseGroups();
      loadSuppliers();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Có lỗi xảy ra');
    }
  };

  const handleDeleteExpense = async (id, expense = null) => {
    // Check if this is a shipping cost expense linked to an import
    const targetExpense = expense || expenses.find(e => e._id === id);
    if (targetExpense?.isShippingCost && targetExpense?.stockImportCode) {
      toast.warning(`Chi phí vận chuyển này được liên kết với phiếu nhập ${targetExpense.stockImportCode}. Để xóa, vui lòng xóa phí vận chuyển trong phiếu nhập hoặc xóa phiếu nhập.`);
      return;
    }
    
    if (!window.confirm('Bạn có chắc muốn xóa khoản thanh toán này?')) return;
    
    try {
      await axios.delete(`${API_URL}/expenses/${id}`);
      setExpenses(prev => prev.filter(e => e._id !== id));
      loadStats();
      toast.success('Đã xóa khoản thanh toán');
    } catch (error) {
      toast.error('Không thể xóa khoản thanh toán');
    }
  };

  // Combine and sort all transactions
  const getAllTransactions = () => {
    const importTx = imports.map(imp => ({
      ...imp,
      type: 'import',
      date: new Date(imp.importDate),
      displayAmount: imp.totalCost
    }));
    
    const expenseTx = expenses.map(exp => ({
      ...exp,
      type: 'expense',
      date: new Date(exp.expenseDate),
      displayAmount: exp.amount
    }));
    
    let all = [...importTx, ...expenseTx];
    
    if (viewMode === 'imports') {
      all = importTx;
    } else if (viewMode === 'expenses') {
      all = expenseTx;
    }
    
    return all.sort((a, b) => b.date - a.date);
  };

  const toggleSelect = (id, type) => {
    if (type === 'expense') {
      setSelectedExpenseIds(prev => 
        prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
      );
    } else {
      setSelectedIds(prev => 
        prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
      );
    }
  };

  const toggleSelectAll = () => {
    if (viewMode === 'expenses') {
      // Toggle all expenses
      if (selectedExpenseIds.length === expenses.length && expenses.length > 0) {
        setSelectedExpenseIds([]);
      } else {
        setSelectedExpenseIds(expenses.map(exp => exp._id));
      }
    } else if (viewMode === 'imports') {
      // Toggle all imports
      if (selectedIds.length === imports.length && imports.length > 0) {
        setSelectedIds([]);
      } else {
        setSelectedIds(imports.map(imp => imp._id));
      }
    } else {
      // Toggle all (both imports and expenses)
      const allTransactions = getAllTransactions();
      const allImports = allTransactions.filter(t => t.type === 'import');
      const allExpenses = allTransactions.filter(t => t.type === 'expense');
      
      const allSelected = selectedIds.length === allImports.length && 
                          selectedExpenseIds.length === allExpenses.length &&
                          (allImports.length > 0 || allExpenses.length > 0);
      
      if (allSelected) {
        setSelectedIds([]);
        setSelectedExpenseIds([]);
      } else {
        setSelectedIds(allImports.map(imp => imp._id));
        setSelectedExpenseIds(allExpenses.map(exp => exp._id));
      }
    }
  };

  const isAllSelected = () => {
    if (viewMode === 'expenses') {
      return selectedExpenseIds.length === expenses.length && expenses.length > 0;
    } else if (viewMode === 'imports') {
      return selectedIds.length === imports.length && imports.length > 0;
    } else {
      const allTransactions = getAllTransactions();
      const allImports = allTransactions.filter(t => t.type === 'import');
      const allExpenses = allTransactions.filter(t => t.type === 'expense');
      return selectedIds.length === allImports.length && 
             selectedExpenseIds.length === allExpenses.length &&
             (allImports.length > 0 || allExpenses.length > 0);
    }
  };

  const totalSelected = selectedIds.length + selectedExpenseIds.length;

  // Long press for mobile bulk select
  const handleTouchStart = (id, type) => {
    const timer = setTimeout(() => {
      if (type === 'expense') {
        if (!selectedExpenseIds.includes(id)) {
          setSelectedExpenseIds([id]);
        }
      } else {
        if (!selectedIds.includes(id)) {
          setSelectedIds([id]);
        }
      }
    }, 500);
    setLongPressTimer(timer);
  };

  const handleTouchEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('vi-VN');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN').format(amount || 0);
  };

  const getItemsDisplay = (items) => {
    if (!items || items.length === 0) return '-';
    const names = items.map(item => 
      item.optionName ? `${item.itemName} (${item.optionName})` : item.itemName
    );
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
  };

  const clearFilters = () => {
    setFilters({ search: '', supplier: '', startDate: '', endDate: '', expenseGroup: '' });
  };

  const hasActiveFilters = filters.search || filters.supplier || filters.startDate || filters.endDate || filters.expenseGroup;

  return (
    <div className="h-full flex flex-col pb-16 md:pb-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <FiDollarSign className="text-green-600" />
          Lịch sử thanh toán
        </h2>

        <div className="flex items-center gap-2 flex-wrap">
          {/* View Mode Toggle */}
          <div className="flex border rounded-lg overflow-hidden text-xs md:text-sm">
            <button
              onClick={() => setViewMode('all')}
              className={`px-2 md:px-3 py-1.5 md:py-2 ${viewMode === 'all' ? 'bg-green-600 text-white' : 'bg-white hover:bg-gray-50'}`}
            >
              Tất cả
            </button>
            <button
              onClick={() => setViewMode('imports')}
              className={`px-2 md:px-3 py-1.5 md:py-2 border-l ${viewMode === 'imports' ? 'bg-green-600 text-white' : 'bg-white hover:bg-gray-50'}`}
            >
              <FiTruck className="inline mr-1" size={12} />
              {!isMobile && 'Nhập hàng'}
            </button>
            <button
              onClick={() => setViewMode('expenses')}
              className={`px-2 md:px-3 py-1.5 md:py-2 border-l ${viewMode === 'expenses' ? 'bg-green-600 text-white' : 'bg-white hover:bg-gray-50'}`}
            >
              <FiCreditCard className="inline mr-1" size={12} />
              {!isMobile && 'Chi phí'}
            </button>
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1 px-2 md:px-3 py-1.5 md:py-2 border rounded-lg hover:bg-gray-50 text-sm ${hasActiveFilters ? 'border-green-500 text-green-600' : ''}`}
          >
            <FiFilter size={16} />
            {!isMobile && 'Lọc'}
            {hasActiveFilters && <span className="w-2 h-2 bg-green-500 rounded-full"></span>}
          </button>
          <button
            onClick={() => openExpenseModal()}
            className="flex items-center gap-1 px-2 md:px-4 py-1.5 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs md:text-sm"
          >
            <FiPlus size={14} />
            <span className="hidden md:inline">Thêm chi phí</span>
          </button>
          <button
            onClick={onCreateNew}
            className="flex items-center gap-1 px-2 md:px-4 py-1.5 md:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs md:text-sm"
          >
            <FiTruck size={14} />
            <span className="hidden md:inline">Nhập hàng</span>
          </button>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {totalSelected > 0 && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-medium text-green-700">{totalSelected} đã chọn</span>
            <button
              onClick={() => { setSelectedIds([]); setSelectedExpenseIds([]); }}
              className="text-sm text-green-600 hover:text-green-800"
            >
              Bỏ chọn
            </button>
          </div>
          <button
            onClick={() => setShowDeleteConfirm({ bulk: true })}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            <FiTrash2 size={16} /> Xóa
          </button>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4">
        <div className="bg-white p-2 md:p-4 rounded-lg md:rounded-xl shadow-sm border">
          <div className="text-gray-500 text-xs md:text-sm">
            {hasActiveFilters ? 'Hôm nay (lọc)' : 'Chi phí hôm nay'}
          </div>
          <div className="text-base md:text-xl font-bold text-blue-600">{formatCurrency(displayStats.today?.totalCost || 0)}đ</div>
          <div className="text-xs text-gray-400">{displayStats.today?.count || 0} phiếu</div>
        </div>
        <div className="bg-white p-2 md:p-4 rounded-lg md:rounded-xl shadow-sm border">
          <div className="text-gray-500 text-xs md:text-sm">
            {hasActiveFilters ? 'Tháng này (lọc)' : 'Chi phí tháng này'}
          </div>
          <div className="text-base md:text-xl font-bold text-green-600">{formatCurrency(displayStats.thisMonth?.totalCost || 0)}đ</div>
          <div className="text-xs text-gray-400">{displayStats.thisMonth?.count || 0} phiếu</div>
        </div>
        <div className="bg-white p-2 md:p-4 rounded-lg md:rounded-xl shadow-sm border">
          <div className="text-gray-500 text-xs md:text-sm">
            {hasActiveFilters ? 'Theo bộ lọc' : 'Tổng chi phí'}
          </div>
          <div className="text-base md:text-xl font-bold text-purple-600">{formatCurrency(displayStats.filtered?.totalCost || 0)}đ</div>
          <div className="text-xs text-gray-400">{displayStats.filtered?.totalImports || 0} phiếu</div>
        </div>
        <div className="bg-white p-2 md:p-4 rounded-lg md:rounded-xl shadow-sm border">
          {viewMode === 'expenses' ? (
            <>
              <div className="text-gray-500 text-xs md:text-sm">Số khoản chi</div>
              <div className="text-base md:text-xl font-bold text-orange-600">{displayStats.filtered?.totalImports || 0}</div>
              <div className="text-xs text-gray-400">khoản thanh toán</div>
            </>
          ) : (
            <>
              <div className="text-gray-500 text-xs md:text-sm">Tổng mặt hàng</div>
              <div className="text-base md:text-xl font-bold text-orange-600">{displayStats.filtered?.totalItems || 0}</div>
              <div className="text-xs text-gray-400">trong {displayStats.filtered?.totalImports || 0} phiếu nhập</div>
            </>
          )}
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-gray-50 border rounded-xl p-4 mb-4 space-y-3">
          {/* Quick Date Filters */}
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="text-sm text-gray-500 mr-2">Lọc nhanh:</span>
            <button
              onClick={() => {
                const today = new Date().toISOString().slice(0, 10);
                setFilters(prev => ({ ...prev, startDate: today, endDate: today }));
              }}
              className={`px-3 py-1 text-sm rounded-full border ${
                filters.startDate === new Date().toISOString().slice(0, 10) && 
                filters.endDate === new Date().toISOString().slice(0, 10)
                  ? 'bg-green-500 text-white border-green-500' 
                  : 'bg-white hover:bg-gray-100'
              }`}
            >
              Hôm nay
            </button>
            <button
              onClick={() => {
                const now = new Date();
                const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
                const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
                setFilters(prev => ({ ...prev, startDate: firstDay, endDate: lastDay }));
              }}
              className={`px-3 py-1 text-sm rounded-full border ${
                (() => {
                  const now = new Date();
                  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
                  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
                  return filters.startDate === firstDay && filters.endDate === lastDay;
                })()
                  ? 'bg-green-500 text-white border-green-500' 
                  : 'bg-white hover:bg-gray-100'
              }`}
            >
              Tháng này
            </button>
            <button
              onClick={() => {
                const now = new Date();
                const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().slice(0, 10);
                const lastDay = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().slice(0, 10);
                setFilters(prev => ({ ...prev, startDate: firstDay, endDate: lastDay }));
              }}
              className="px-3 py-1 text-sm rounded-full border bg-white hover:bg-gray-100"
            >
              Tháng trước
            </button>
            <button
              onClick={() => {
                const now = new Date();
                const sevenDaysAgo = new Date(now.setDate(now.getDate() - 7)).toISOString().slice(0, 10);
                const today = new Date().toISOString().slice(0, 10);
                setFilters(prev => ({ ...prev, startDate: sevenDaysAgo, endDate: today }));
              }}
              className="px-3 py-1 text-sm rounded-full border bg-white hover:bg-gray-100"
            >
              7 ngày qua
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Tìm kiếm</label>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  placeholder="Mã phiếu, tên hàng..."
                  className="w-full pl-10 pr-3 py-2 border rounded-lg text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Nhà cung cấp</label>
              <select
                value={filters.supplier}
                onChange={(e) => setFilters(prev => ({ ...prev, supplier: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              >
                <option value="">Tất cả NCC</option>
                {suppliersList.map(sup => (
                  <option key={sup._id} value={sup.name}>{sup.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Nhóm chi phí</label>
              <select
                value={filters.expenseGroup}
                onChange={(e) => setFilters(prev => ({ ...prev, expenseGroup: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              >
                <option value="">Tất cả nhóm</option>
                {expenseGroups.map(group => (
                  <option key={group._id} value={group._id}>{group.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Từ ngày</label>
              <div className="relative">
                <input
                  type="text"
                  readOnly
                  value={filters.startDate ? (() => {
                    const [y, m, d] = filters.startDate.split('-');
                    return `${d}/${m}/${y}`;
                  })() : ''}
                  onClick={(e) => e.target.nextSibling.showPicker()}
                  placeholder="Ngày/Tháng/Năm"
                  className="w-full px-3 py-2 border rounded-lg text-sm cursor-pointer placeholder:text-gray-300 placeholder:text-xs"
                />
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Đến ngày</label>
              <div className="relative">
                <input
                  type="text"
                  readOnly
                  value={filters.endDate ? (() => {
                    const [y, m, d] = filters.endDate.split('-');
                    return `${d}/${m}/${y}`;
                  })() : ''}
                  onClick={(e) => e.target.nextSibling.showPicker()}
                  placeholder="Ngày/Tháng/Năm"
                  className="w-full px-3 py-2 border rounded-lg text-sm cursor-pointer placeholder:text-gray-300 placeholder:text-xs"
                />
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
              </div>
            </div>
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Xóa bộ lọc
            </button>
          )}
        </div>
      )}

      {/* Table/List */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        </div>
      ) : imports.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
          <FiTruck size={48} className="mb-2 opacity-50" />
          <p>Chưa có phiếu nhập nào</p>
        </div>
      ) : (
        <div className="flex-1 overflow-auto" ref={listRef}>
          {/* Desktop Table */}
          {!isMobile ? (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left w-10">
                      <input
                        type="checkbox"
                        checked={isAllSelected()}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 text-green-600 rounded"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Loại</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mã/Tên</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ngày</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Chi tiết</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Người nhận</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Thanh toán</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Số tiền</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">File</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {getAllTransactions().map((tx, index) => (
                    <tr 
                      key={tx._id}
                      ref={tx.type === 'import' && index === getAllTransactions().filter(t => t.type === 'import').length - 1 ? lastItemRef : null}
                      className={`hover:bg-gray-50 ${(tx.type === 'import' ? selectedIds.includes(tx._id) : selectedExpenseIds.includes(tx._id)) ? 'bg-green-50' : ''}`}
                    >
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={tx.type === 'import' ? selectedIds.includes(tx._id) : selectedExpenseIds.includes(tx._id)}
                          onChange={() => toggleSelect(tx._id, tx.type)}
                          className="w-4 h-4 text-green-600 rounded"
                        />
                      </td>
                      <td className="px-4 py-4">
                        {tx.type === 'import' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-green-100 text-green-700">
                            <FiTruck size={12} /> Nhập hàng
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-blue-100 text-blue-700">
                            <FiCreditCard size={12} /> Chi phí
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {tx.type === 'import' ? (
                          <span className="font-mono text-sm text-blue-600">{tx.importCode}</span>
                        ) : (
                          <span className="text-sm font-medium">{tx.name}</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">{formatDate(tx.type === 'import' ? tx.importDate : tx.expenseDate)}</td>
                      <td className="px-4 py-4 text-sm max-w-xs truncate">
                        {tx.type === 'import' ? getItemsDisplay(tx.items) : (tx.expenseGroupName || tx.expenseGroup?.name || '-')}
                        {tx.type === 'expense' && tx.isShippingCost && tx.stockImportCode && (
                          <span className="text-xs text-orange-600 ml-1">
                            ({tx.stockImportCode})
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {tx.type === 'import' ? (tx.supplier || '-') : (tx.supplierId?.name || tx.recipient || '-')}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <span className={`px-2 py-1 rounded text-xs ${
                          tx.paymentMethod === 'cash' ? 'bg-green-100 text-green-700' :
                          tx.paymentMethod === 'transfer' ? 'bg-blue-100 text-blue-700' :
                          tx.paymentMethod === 'debt' ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {tx.paymentMethod === 'cash' && 'Tiền mặt'}
                          {tx.paymentMethod === 'transfer' && 'CK'}
                          {tx.paymentMethod === 'debt' && 'Công nợ'}
                          {!tx.paymentMethod && '-'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-right font-medium text-green-600">{formatCurrency(tx.displayAmount)}đ</td>
                      <td className="px-4 py-4 text-center">
                        {(tx.type === 'import' && tx.invoiceImage) || (tx.type === 'expense' && tx.attachmentUrl) ? (
                          <button
                            onClick={() => tx.type === 'import' ? setViewingInvoice(tx) : window.open(`${API_URL.replace('/api', '')}${tx.attachmentUrl}`, '_blank')}
                            className="text-blue-600 hover:text-blue-700"
                            title="Xem file"
                          >
                            <FiFileText size={18} />
                          </button>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-1">
                          {tx.type === 'import' ? (
                            <>
                              <button
                                onClick={() => setViewingImport(tx)}
                                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                                title="Xem chi tiết"
                              >
                                <FiEye size={16} />
                              </button>
                              <button
                                onClick={() => setEditingImport(tx)}
                                className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded"
                                title="Sửa"
                              >
                                <FiEdit2 size={16} />
                              </button>
                              <button
                                onClick={() => setShowDeleteConfirm({ id: tx._id, code: tx.importCode })}
                                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                                title="Xóa"
                              >
                                <FiTrash2 size={16} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => openExpenseModal(tx)}
                                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                                title="Sửa"
                              >
                                <FiEdit2 size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteExpense(tx._id, tx)}
                                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                                title="Xóa"
                              >
                                <FiTrash2 size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            </div>
          ) : (
            /* Mobile Cards */
            <div className="space-y-2">
              {getAllTransactions().map((tx, index) => (
                <div
                  key={tx._id}
                  ref={tx.type === 'import' && index === getAllTransactions().filter(t => t.type === 'import').length - 1 ? lastItemRef : null}
                  onTouchStart={() => handleTouchStart(tx._id, tx.type)}
                  onTouchEnd={handleTouchEnd}
                  onTouchMove={handleTouchEnd}
                  className={`bg-white border rounded-xl p-3 ${(tx.type === 'import' ? selectedIds.includes(tx._id) : selectedExpenseIds.includes(tx._id)) ? 'border-green-500 bg-green-50' : ''}`}
                  onClick={() => {
                    if (totalSelected > 0) {
                      toggleSelect(tx._id, tx.type);
                    } else if (tx.type === 'import') {
                      setEditingImport(tx);
                    } else {
                      openExpenseModal(tx);
                    }
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {totalSelected > 0 && (
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${(tx.type === 'import' ? selectedIds.includes(tx._id) : selectedExpenseIds.includes(tx._id)) ? 'bg-green-500 border-green-500' : 'border-gray-300'}`}>
                          {(tx.type === 'import' ? selectedIds.includes(tx._id) : selectedExpenseIds.includes(tx._id)) && <FiCheck className="text-white" size={12} />}
                        </div>
                      )}
                      {tx.type === 'import' ? (
                        <>
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-700">
                            <FiTruck size={10} className="mr-1" /> Nhập
                          </span>
                          <span className="font-mono text-sm text-blue-600">{tx.importCode}</span>
                        </>
                      ) : (
                        <>
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                            <FiCreditCard size={10} className="mr-1" /> Chi phí
                          </span>
                          <span className="text-sm font-medium">{tx.name}</span>
                        </>
                      )}
                      <span className="text-xs text-gray-400">{formatDate(tx.type === 'import' ? tx.importDate : tx.expenseDate)}</span>
                    </div>
                    <p className="text-sm truncate">
                      {tx.type === 'import' ? getItemsDisplay(tx.items) : (tx.expenseGroupName || tx.expenseGroup?.name || tx.notes || '-')}
                      {tx.type === 'expense' && tx.isShippingCost && tx.stockImportCode && (
                        <span className="text-xs text-orange-600 ml-2">
                          (Phiếu nhập: {tx.stockImportCode})
                        </span>
                      )}
                    </p>
                    <div className="flex items-center justify-between mt-1">
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        {tx.type === 'import' && tx.supplier && <span>NCC: {tx.supplier}</span>}
                        {tx.type === 'expense' && (tx.supplierId?.name || tx.recipient) && <span>Người nhận: {tx.supplierId?.name || tx.recipient}</span>}
                        <span className="font-medium text-green-600">{formatCurrency(tx.displayAmount)}đ</span>
                      </div>
                      {totalSelected === 0 && (
                        <div className="flex items-center gap-1">
                          {((tx.type === 'import' && tx.invoiceImage) || (tx.type === 'expense' && tx.attachmentUrl)) && (
                            <button
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                if (tx.type === 'import') {
                                  setViewingInvoice(tx);
                                } else {
                                  window.open(`${API_URL.replace('/api', '')}${tx.attachmentUrl}`, '_blank');
                                }
                              }}
                              className="p-1.5 text-blue-600"
                            >
                              <FiFileText size={16} />
                            </button>
                          )}
                          <button
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              if (tx.type === 'import') {
                                setShowDeleteConfirm({ id: tx._id, code: tx.importCode });
                              } else {
                                handleDeleteExpense(tx._id, tx);
                              }
                            }}
                            className="p-1.5 text-red-500"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {loadingMore && (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
            </div>
          )}
        </div>
      )}

      {/* View Import Modal */}
      {viewingImport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4 pb-20 md:pb-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[85vh] md:max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
              <h2 className="font-semibold">Chi tiết phiếu nhập</h2>
              <button onClick={() => setViewingImport(null)} className="text-gray-400 hover:text-gray-600">
                <FiX size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Mã phiếu:</span>
                  <p className="font-mono font-medium">{viewingImport.importCode}</p>
                </div>
                <div>
                  <span className="text-gray-500">Ngày nhập:</span>
                  <p className="font-medium">{formatDate(viewingImport.importDate)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Nhà cung cấp:</span>
                  <p className="font-medium">{viewingImport.supplier || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Thanh toán:</span>
                  <p className="font-medium">
                    {viewingImport.paymentMethod === 'cash' && 'Tiền mặt'}
                    {viewingImport.paymentMethod === 'transfer' && 'Chuyển khoản'}
                    {viewingImport.paymentMethod === 'debt' && 'Công nợ'}
                    {!viewingImport.paymentMethod && '-'}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Tổng tiền hàng:</span>
                  <p className="font-medium text-green-600">{formatCurrency(viewingImport.totalCost)}đ</p>
                </div>
                {viewingImport.shippingCost > 0 && (
                  <div>
                    <span className="text-gray-500">
                      <FiTruck className="inline mr-1" size={14} />
                      Phí vận chuyển:
                    </span>
                    <p className="font-medium text-orange-600">{formatCurrency(viewingImport.shippingCost)}đ</p>
                    <p className="text-xs text-gray-400">
                      (Đã tính vào chi phí khác)
                    </p>
                  </div>
                )}
                {viewingImport.shippingCost > 0 && (
                  <div className="pt-2 border-t">
                    <span className="text-gray-500">Tổng chi phí:</span>
                    <p className="font-bold text-lg text-blue-600">{formatCurrency(viewingImport.totalCost + viewingImport.shippingCost)}đ</p>
                  </div>
                )}
              </div>

              <div>
                <h3 className="font-medium mb-2">Danh sách hàng ({viewingImport.items?.length || 0})</h3>
                <div className="space-y-2">
                  {viewingImport.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                      <div>
                        <span className="font-medium">{item.itemName}</span>
                        {item.optionName && <span className="text-gray-500 ml-1">({item.optionName})</span>}
                        <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${item.itemType === 'supply' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                          {item.itemType === 'supply' ? 'Vật dụng' : 'Nguyên liệu'}
                        </span>
                      </div>
                      <div className="text-right">
                        <p>{item.quantity} {item.unit}</p>
                        {item.price > 0 && <p className="text-gray-500">{formatCurrency(item.totalPrice)}đ</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {viewingImport.notes && (
                <div>
                  <span className="text-gray-500 text-sm">Ghi chú:</span>
                  <p className="text-sm">{viewingImport.notes}</p>
                </div>
              )}

              {viewingImport.invoiceImage && (
                <button
                  onClick={() => setViewingInvoice(viewingImport)}
                  className="w-full flex items-center justify-center gap-2 py-2 border rounded-lg hover:bg-gray-50"
                >
                  <FiFileText /> Xem hóa đơn
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Import Modal */}
      {editingImport && (
        <EditImportModal
          importData={editingImport}
          onClose={() => setEditingImport(null)}
          onSave={handleUpdateImport}
        />
      )}

      {/* View Invoice Modal */}
      {viewingInvoice && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4 pb-20 md:pb-4">
          <div className={`relative ${viewingInvoice.invoiceImage?.endsWith('.pdf') ? 'w-full max-w-5xl' : 'max-w-4xl'} max-h-[85vh] md:max-h-[90vh] overflow-auto`}>
            <button
              onClick={() => setViewingInvoice(null)}
              className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 z-10"
            >
              <FiX size={20} />
            </button>
            {viewingInvoice.invoiceImage?.endsWith('.pdf') ? (
              <iframe
                src={`${API_URL.replace('/api', '')}${viewingInvoice.invoiceImage}`}
                className="w-full min-w-[320px] md:min-w-[800px] h-[80vh] bg-white rounded-lg"
                title="Invoice PDF"
              />
            ) : (
              <img
                src={`${API_URL.replace('/api', '')}${viewingInvoice.invoiceImage}`}
                alt="Hóa đơn"
                className="max-w-full rounded-lg"
              />
            )}
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4 pb-20 md:pb-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-4">
            <h3 className="font-semibold mb-2">
              {showDeleteConfirm.bulk 
                ? `Xóa ${totalSelected} mục đã chọn?` 
                : `Xóa ${showDeleteConfirm.type === 'expense' ? 'khoản chi' : 'phiếu'} ${showDeleteConfirm.code}?`}
            </h3>
            {(selectedIds.length > 0 || !showDeleteConfirm.bulk) && showDeleteConfirm.type !== 'expense' && (
              <p className="text-sm text-gray-500 mb-4">
                Bạn có muốn hoàn lại số lượng kho khi xóa phiếu nhập?
              </p>
            )}
            <div className="space-y-2">
              {(selectedIds.length > 0 || (!showDeleteConfirm.bulk && showDeleteConfirm.type !== 'expense')) && (
                <button
                  onClick={() => showDeleteConfirm.bulk ? handleBulkDelete(true) : handleDelete(showDeleteConfirm.id, true)}
                  className="w-full py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                >
                  Xóa và hoàn kho
                </button>
              )}
              <button
                onClick={() => showDeleteConfirm.bulk ? handleBulkDelete(false) : handleDelete(showDeleteConfirm.id, false)}
                className="w-full py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                {(selectedIds.length > 0 || (!showDeleteConfirm.bulk && showDeleteConfirm.type !== 'expense')) ? 'Xóa không hoàn kho' : 'Xóa'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="w-full py-2 border rounded-lg hover:bg-gray-50"
              >
                Hủy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expense Modal */}
      {showExpenseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 pb-20 md:pb-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] md:max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
              <h3 className="text-lg font-bold">
                {editingExpense ? 'Sửa khoản thanh toán' : 'Thêm khoản thanh toán'}
              </h3>
              <button onClick={closeExpenseModal} className="p-2 hover:bg-gray-100 rounded-full">
                <FiX size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-1">Tên khoản thanh toán <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={expenseForm.name}
                  onChange={(e) => setExpenseForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="VD: Tiền điện tháng 12"
                />
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium mb-1">Số tiền <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm(prev => ({ ...prev, amount: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
              </div>

              {/* Date and Expense Group */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Ngày thanh toán</label>
                  <div 
                    className="relative cursor-pointer"
                    onClick={() => {
                      const dateInput = document.getElementById('expense-date-picker');
                      if (dateInput) dateInput.showPicker();
                    }}
                  >
                    <input
                      type="text"
                      readOnly
                      value={expenseForm.expenseDate ? (() => {
                        const [y, m, d] = expenseForm.expenseDate.split('-');
                        return `${d}/${m}/${y}`;
                      })() : ''}
                      className="w-full px-3 py-2 pr-10 border rounded-lg bg-white cursor-pointer placeholder:text-gray-300 placeholder:text-xs"
                      placeholder="Ngày/Tháng/Năm"
                    />
                    <input
                      id="expense-date-picker"
                      type="date"
                      value={expenseForm.expenseDate}
                      onChange={(e) => setExpenseForm(prev => ({ ...prev, expenseDate: e.target.value }))}
                      className="sr-only"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                      <FiCalendar size={18} />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Nhóm thanh toán</label>
                  {showAddExpenseGroup ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newExpenseGroupName}
                        onChange={(e) => setNewExpenseGroupName(e.target.value)}
                        className="flex-1 min-w-0 px-3 py-2 border rounded-lg text-sm"
                        placeholder="Tên nhóm mới"
                        autoFocus
                      />
                      <button
                        onClick={() => {
                          setShowAddExpenseGroup(false);
                          setNewExpenseGroupName('');
                        }}
                        className="px-2 text-gray-500 hover:text-gray-700"
                      >
                        <FiX size={18} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-1">
                      <select
                        value={expenseForm.expenseGroup}
                        onChange={(e) => {
                          const group = expenseGroups.find(g => g._id === e.target.value);
                          setExpenseForm(prev => ({ 
                            ...prev, 
                            expenseGroup: e.target.value,
                            expenseGroupName: group?.name || ''
                          }));
                        }}
                        className="flex-1 min-w-0 px-2 py-2 border rounded-lg text-sm"
                      >
                        <option value="">-- Chọn nhóm --</option>
                        {expenseGroups.map(group => (
                          <option key={group._id} value={group._id}>{group.name}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => setShowAddExpenseGroup(true)}
                        className="px-2 py-2 text-blue-600 hover:bg-blue-50 rounded-lg text-sm shrink-0"
                      >
                        + Thêm
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Recipient/Supplier */}
              <div>
                <label className="block text-sm font-medium mb-1">Người nhận</label>
                {showAddExpenseSupplier ? (
                  <div className="flex gap-1">
                    <input
                      type="text"
                      value={newExpenseSupplierName}
                      onChange={(e) => setNewExpenseSupplierName(e.target.value)}
                      className="flex-1 min-w-0 px-3 py-2 border rounded-lg text-sm"
                      placeholder="Tên người nhận mới"
                      autoFocus
                    />
                    <button
                      onClick={() => {
                        setShowAddExpenseSupplier(false);
                        setNewExpenseSupplierName('');
                      }}
                      className="px-2 text-gray-500 hover:text-gray-700 shrink-0"
                    >
                      <FiX size={18} />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-1">
                    <select
                      value={expenseForm.supplierId}
                      onChange={(e) => setExpenseForm(prev => ({ ...prev, supplierId: e.target.value }))}
                      className="flex-1 min-w-0 px-2 py-2 border rounded-lg text-sm"
                    >
                      <option value="">-- Chọn người nhận --</option>
                      {salesAccounts.length > 0 && (
                        <optgroup label="Nhân viên">
                          {salesAccounts.map((acc) => (
                            <option key={`staff-${acc._id}`} value={`staff:${acc.displayName}`}>
                              {acc.displayName}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {suppliersList.length > 0 && (
                        <optgroup label="Nhà cung cấp">
                          {suppliersList.map((s, idx) => (
                            <option key={s._id || `supplier-${idx}`} value={s._id || s.name}>{s.name}</option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                    <button
                      onClick={() => setShowAddExpenseSupplier(true)}
                      className="px-2 py-2 text-blue-600 hover:bg-blue-50 rounded-lg text-sm shrink-0"
                    >
                      + Thêm
                    </button>
                  </div>
                )}
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium mb-1">Phương thức thanh toán</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setExpenseForm(prev => ({ ...prev, paymentMethod: 'cash' }))}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm border ${
                      expenseForm.paymentMethod === 'cash'
                        ? 'bg-green-500 text-white border-green-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Tiền mặt
                  </button>
                  <button
                    type="button"
                    onClick={() => setExpenseForm(prev => ({ ...prev, paymentMethod: 'transfer' }))}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm border ${
                      expenseForm.paymentMethod === 'transfer'
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Chuyển khoản
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium mb-1">Ghi chú</label>
                <textarea
                  value={expenseForm.notes}
                  onChange={(e) => setExpenseForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg resize-none"
                  rows={2}
                  placeholder="Ghi chú thêm..."
                />
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium mb-1">File đính kèm</label>
                <div className="border-2 border-dashed rounded-lg p-4">
                  <input
                    type="file"
                    id="expense-file"
                    onChange={(e) => setExpenseFile(e.target.files[0])}
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                  />
                  <label
                    htmlFor="expense-file"
                    className="flex flex-col items-center cursor-pointer text-gray-500 hover:text-gray-700"
                  >
                    <FiUpload size={24} className="mb-2" />
                    <span className="text-sm">
                      {expenseFile ? expenseFile.name : (editingExpense?.attachmentName || 'Tải lên file')}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 p-4 border-t sticky bottom-0 bg-white">
              <button
                onClick={closeExpenseModal}
                className="flex-1 py-2 border rounded-lg hover:bg-gray-50 text-sm"
              >
                Hủy
              </button>
              <button
                onClick={handleSaveExpense}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                {editingExpense ? 'Lưu' : 'Thêm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Edit Import Modal Component
const EditImportModal = ({ importData, onClose, onSave }) => {
  // Determine initial item type based on existing items
  const getInitialItemType = () => {
    if (importData.items && importData.items.length > 0) {
      return importData.items[0].itemType || 'supply';
    }
    return 'supply';
  };

  const [form, setForm] = useState({
    importCode: importData.importCode || '',
    supplier: importData.supplier || '',
    notes: importData.notes || '',
    importDate: importData.importDate ? new Date(importData.importDate).toISOString().slice(0, 10) : '',
    paymentMethod: importData.paymentMethod || 'cash',
    shippingCost: importData.shippingCost?.toString() || '',
    items: importData.items?.map(item => ({
      ...item,
      type: item.itemType,
      displayUnit: item.unit
    })) || []
  });
  const [newInvoice, setNewInvoice] = useState(null);
  const [removeInvoice, setRemoveInvoice] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updateStock, setUpdateStock] = useState(false);
  
  // Payment method labels
  const paymentMethodLabels = {
    cash: 'Tiền mặt',
    transfer: 'Chuyển khoản',
    debt: 'Công nợ'
  };
  
  // For adding new items
  const [supplies, setSupplies] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [selectedItem, setSelectedItem] = useState({
    type: getInitialItemType(),
    itemId: '',
    optionName: '',
    quantity: 1,
    price: 0
  });
  
  // Suppliers
  const [suppliersList, setSuppliersList] = useState([]);
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');
  
  const fileInputRef = useRef(null);

  // Unit labels
  const unitLabels = { piece: 'Cái', kg: 'Kg', box: 'Hộp', bag: 'Túi', bottle: 'Chai', can: 'Lon' };
  const packagingLabels = { box: 'Hộp', can: 'Lon', bottle: 'Chai', bag: 'Túi', piece: 'Quả', item: 'Cái', kg: 'Kg' };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [supRes, ingRes, suppRes] = await Promise.all([
          axios.get(`${API_URL}/supplies`, { params: { limit: 0 } }),
          axios.get(`${API_URL}/ingredients`, { params: { limit: 0 } }),
          axios.get(`${API_URL}/suppliers`)
        ]);
        setSupplies(supRes.data.data || []);
        setIngredients(ingRes.data.data || []);
        setSuppliersList((suppRes.data.data || []).filter(s => !s.isFromImport));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN').format(amount || 0);
  };
  
  const handleAddSupplier = async () => {
    if (!newSupplierName.trim()) {
      toast.error('Vui lòng nhập tên NCC');
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/suppliers`, { 
        name: newSupplierName.trim() 
      });
      const newSupplier = response.data.data;
      setSuppliersList(prev => [...prev, newSupplier]);
      setForm(prev => ({ ...prev, supplier: newSupplier.name }));
      setNewSupplierName('');
      setShowAddSupplier(false);
      toast.success('Đã thêm NCC');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể thêm NCC');
    }
  };

  const getSupplyOptionsForSelect = () => {
    const options = [];
    supplies.forEach(supply => {
      if (supply.options && supply.options.length > 0) {
        supply.options.forEach(opt => {
          options.push({
            id: supply._id,
            optionName: opt.name,
            label: `${supply.name} (${opt.name})`,
            supply
          });
        });
      } else {
        options.push({
          id: supply._id,
          optionName: '',
          label: supply.name,
          supply
        });
      }
    });
    return options;
  };

  const getSelectedItemUnit = () => {
    if (!selectedItem.itemId) return '';
    
    if (selectedItem.type === 'supply') {
      const supply = supplies.find(s => s._id === selectedItem.itemId);
      if (supply) return unitLabels[supply.unit] || supply.unit;
    } else {
      const ingredient = ingredients.find(i => i._id === selectedItem.itemId);
      if (ingredient) return packagingLabels[ingredient.packagingUnit] || ingredient.packagingUnit;
    }
    return '';
  };

  const addItem = () => {
    if (!selectedItem.itemId) {
      toast.error('Vui lòng chọn mặt hàng');
      return;
    }

    let itemName = '';
    let displayUnit = '';

    if (selectedItem.type === 'supply') {
      const supply = supplies.find(s => s._id === selectedItem.itemId);
      if (supply) {
        itemName = supply.name + (selectedItem.optionName ? ` (${selectedItem.optionName})` : '');
        displayUnit = unitLabels[supply.unit] || supply.unit;
      }
    } else {
      const ingredient = ingredients.find(i => i._id === selectedItem.itemId);
      if (ingredient) {
        itemName = ingredient.name;
        displayUnit = packagingLabels[ingredient.packagingUnit] || ingredient.packagingUnit;
      }
    }

    setForm(prev => ({
      ...prev,
      items: [...prev.items, {
        itemType: selectedItem.type,
        itemId: selectedItem.itemId,
        itemName,
        optionName: selectedItem.optionName || null,
        quantity: selectedItem.quantity,
        unit: displayUnit,
        displayUnit,
        price: selectedItem.price,
        totalPrice: selectedItem.quantity * selectedItem.price
      }]
    }));

    setSelectedItem(prev => ({
      type: prev.type,
      itemId: '',
      optionName: '',
      quantity: 1,
      price: 0
    }));
  };

  const removeItem = (index) => {
    setForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    if (form.items.length === 0) {
      toast.error('Vui lòng thêm ít nhất 1 mặt hàng');
      return;
    }
    
    if (!form.importCode.trim()) {
      toast.error('Vui lòng nhập mã phiếu');
      return;
    }
    
    setSaving(true);
    await onSave(importData._id, {
      importCode: form.importCode.trim(),
      supplier: form.supplier,
      notes: form.notes,
      importDate: form.importDate,
      paymentMethod: form.paymentMethod,
      shippingCost: form.shippingCost || 0,
      items: form.items,
      updateStock,
      invoice: newInvoice,
      removeInvoice
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4 pb-20 md:pb-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[85vh] md:max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
          <div>
            <h2 className="font-semibold">Sửa phiếu nhập</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FiX size={20} />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          {/* Import Code */}
          <div>
            <label className="block text-sm font-medium mb-1">Mã phiếu nhập <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form.importCode}
              onChange={(e) => setForm(prev => ({ ...prev, importCode: e.target.value.toUpperCase() }))}
              className="w-full px-3 py-2 border rounded-lg font-mono"
              placeholder="VD: IMP20241212001"
            />
          </div>
          
          {/* Add Item Section */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="font-medium text-sm text-blue-700">Thêm mặt hàng</p>
              <div className="flex border rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setSelectedItem(prev => ({ ...prev, type: 'supply', itemId: '', optionName: '' }))}
                  className={`px-3 py-1.5 text-xs ${selectedItem.type === 'supply' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`}
                >
                  Vật dụng
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedItem(prev => ({ ...prev, type: 'ingredient', itemId: '', optionName: '' }))}
                  className={`px-3 py-1.5 text-xs border-l ${selectedItem.type === 'ingredient' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600'}`}
                >
                  Nguyên liệu
                </button>
              </div>
            </div>
            
            <div>
              {selectedItem.type === 'supply' ? (
                <ItemSearchSelect
                  itemType="supply"
                  supplyOptions={getSupplyOptionsForSelect()}
                  value={selectedItem.itemId}
                  optionValue={selectedItem.optionName}
                  onChange={(item) => {
                    if (item) {
                      setSelectedItem(prev => ({ 
                        ...prev, 
                        itemId: item.id, 
                        optionName: item.optionName || '' 
                      }));
                    } else {
                      setSelectedItem(prev => ({ ...prev, itemId: '', optionName: '' }));
                    }
                  }}
                  placeholder="Tìm vật dụng..."
                  showUnit={false}
                />
              ) : (
                <ItemSearchSelect
                  itemType="ingredient"
                  items={ingredients}
                  value={selectedItem.itemId}
                  onChange={(item) => {
                    if (item) {
                      setSelectedItem(prev => ({ ...prev, itemId: item._id }));
                    } else {
                      setSelectedItem(prev => ({ ...prev, itemId: '' }));
                    }
                  }}
                  placeholder="Tìm nguyên liệu..."
                  showUnit={false}
                />
              )}
            </div>
            
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-gray-500">
                  Số lượng{getSelectedItemUnit() ? ` (${getSelectedItemUnit()})` : ''}
                </label>
                <input
                  type="number"
                  step="any"
                  value={selectedItem.quantity}
                  onChange={(e) => setSelectedItem(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 1 }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  min="0.01"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Đơn giá (đ)</label>
                <input
                  type="number"
                  value={selectedItem.price}
                  onChange={(e) => setSelectedItem(prev => ({ ...prev, price: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  min="0"
                />
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={addItem}
                  disabled={!selectedItem.itemId}
                  className="w-full py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  + Thêm
                </button>
              </div>
            </div>
          </div>

          {/* Items List */}
          {form.items.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-green-50 border-b flex justify-between items-center">
                <span className="font-medium text-sm text-green-700">
                  Danh sách ({form.items.length} mặt hàng)
                </span>
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, items: [] }))}
                  className="text-xs text-red-500 hover:text-red-700"
                >
                  Xóa tất cả
                </button>
              </div>
              <div className="divide-y max-h-48 overflow-y-auto">
                {form.items.map((item, idx) => (
                  <div key={idx} className="p-3 flex justify-between items-center hover:bg-gray-50">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{item.itemName}</p>
                      <p className="text-xs text-gray-500">
                        {item.quantity} {item.displayUnit || item.unit} × {formatCurrency(item.price)}đ
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <span className="font-semibold text-green-600 whitespace-nowrap">
                        {formatCurrency(item.totalPrice)}đ
                      </span>
                      <button
                        type="button"
                        onClick={() => removeItem(idx)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <FiX size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-3 bg-gray-50 flex justify-between border-t">
                <span className="font-medium">Tổng cộng:</span>
                <span className="font-bold text-green-600">
                  {formatCurrency(form.items.reduce((sum, i) => sum + (i.totalPrice || 0), 0))}đ
                </span>
              </div>
            </div>
          )}
          
          {/* Additional Info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Ngày nhập</label>
              <div 
                className="relative cursor-pointer"
                onClick={() => {
                  const dateInput = document.getElementById('edit-import-date');
                  if (dateInput) {
                    dateInput.showPicker();
                  }
                }}
              >
                <input
                  type="text"
                  readOnly
                  value={form.importDate ? (() => {
                    const [y, m, d] = form.importDate.split('-');
                    return `${d}/${m}/${y}`;
                  })() : ''}
                  className="w-full px-3 py-2 pr-10 border rounded-lg bg-white cursor-pointer placeholder:text-gray-300 placeholder:text-xs"
                  placeholder="Ngày/Tháng/Năm"
                />
                <input
                  id="edit-import-date"
                  type="date"
                  value={form.importDate}
                  onChange={(e) => setForm(prev => ({ ...prev, importDate: e.target.value }))}
                  className="sr-only"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Nhà cung cấp</label>
              {showAddSupplier ? (
                <div className="flex gap-1">
                  <input
                    type="text"
                    value={newSupplierName}
                    onChange={(e) => setNewSupplierName(e.target.value)}
                    className="flex-1 min-w-0 px-2 py-2 border rounded-lg text-sm"
                    placeholder="Tên NCC mới"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={handleAddSupplier}
                    className="px-2 py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 shrink-0"
                  >
                    Lưu
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowAddSupplier(false); setNewSupplierName(''); }}
                    className="px-2 py-2 border rounded-lg text-sm hover:bg-gray-50 shrink-0"
                  >
                    Hủy
                  </button>
                </div>
              ) : (
                <div className="flex gap-1">
                  <select
                    value={form.supplier}
                    onChange={(e) => setForm(prev => ({ ...prev, supplier: e.target.value }))}
                    className="flex-1 min-w-0 px-2 py-2 border rounded-lg text-sm"
                  >
                    <option value="">-- Chọn NCC --</option>
                    {suppliersList.map(s => (
                      <option key={s._id} value={s.name}>{s.name}</option>
                    ))}
                    {/* Show current supplier if not in list */}
                    {form.supplier && !suppliersList.find(s => s.name === form.supplier) && (
                      <option value={form.supplier}>{form.supplier}</option>
                    )}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowAddSupplier(true)}
                    className="px-2 py-2 border rounded-lg text-green-600 hover:bg-green-50 text-sm shrink-0"
                  >
                    + Thêm
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium mb-1">Phương thức thanh toán</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(paymentMethodLabels).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, paymentMethod: value }))}
                  className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                    form.paymentMethod === value
                      ? 'bg-green-500 text-white border-green-500'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Shipping Cost */}
          <div>
            <label className="block text-sm font-medium mb-1">
              <FiTruck className="inline mr-1" size={14} />
              Phí vận chuyển
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={form.shippingCost}
              onChange={(e) => {
                const val = e.target.value.replace(/[^\d]/g, '');
                setForm(prev => ({ ...prev, shippingCost: val }));
              }}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="0"
            />
            <p className="text-xs text-gray-500 mt-1">
              * Phí vận chuyển sẽ tự động được tính vào chi phí khác
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Ghi chú</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg"
              rows={2}
            />
          </div>
          
          {/* Invoice */}
          <div>
            <label className="block text-sm font-medium mb-1">Hóa đơn</label>
            {importData.invoiceImage && !removeInvoice ? (
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border">
                <FiFileText className="text-blue-600" />
                <span className="text-sm flex-1 truncate">Đã có hóa đơn</span>
                <button
                  type="button"
                  onClick={() => setRemoveInvoice(true)}
                  className="text-red-500 text-sm hover:underline"
                >
                  Xóa
                </button>
              </div>
            ) : (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => setNewInvoice(e.target.files[0])}
                  accept="image/*,.pdf"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 flex items-center justify-center gap-2 text-gray-600"
                >
                  <FiUpload size={16} />
                  {newInvoice ? newInvoice.name : 'Tải lên hóa đơn'}
                </button>
              </>
            )}
          </div>
          
          {/* Update Stock Option */}
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={updateStock}
                onChange={(e) => setUpdateStock(e.target.checked)}
                className="w-4 h-4 text-yellow-600 rounded"
              />
              <span className="text-sm text-yellow-700">
                Cập nhật số lượng tồn kho (hoàn tác số cũ, áp dụng số mới)
              </span>
            </label>
          </div>
        </div>
        
        <div className="flex gap-3 p-4 border-t sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
          >
            Hủy
          </button>
          <button
            onClick={handleSave}
            disabled={saving || form.items.length === 0}
            className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {saving ? 'Đang lưu...' : 'Lưu thay đổi'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StockImportsManagement;
